import {useMemo} from 'react'
import {meta} from 'tizra'
import * as B from '../block'
import {defaultConfig, TocAdmin} from './admin'

// "bookTizraId": "c2b",
// "children": [],
// "entryId": 2,
// "filePageNumber": 8,
// "hasChildren": false,
// "id": "bookmark-38967",
// "isDisplayed": true,
// "level": 0,
// "logicalPage": "vii",
// "metaType": "toc-entry",
// "pageNumber": 8,
// "procProps": {},
// "rawProps": {},
// "title": "brief contents"

const TocEntryRow = ({entry, ...props}) => (
  <B.Box
    as={B.UniversalLink}
    href={meta.href(entry)}
    display="flex"
    alignItems="baseline"
    px="1rem"
    {...props}
  />
)

const TocEntryText = props => (
  <B.Text
    font="body"
    m={0}
    {...props}
    style={{overflow: 'hidden', whiteSpace: 'nowrap', ...props.style}}
  />
)

const TocEntryTitle = ({level, title, ...props}) => (
  <TocEntryText
    flex="0 1 100%"
    pl={`${level}rem`}
    fontWeight={level === 0 ? 'bold' : 'regular'}
    {...props}
    style={{
      textOverflow: 'ellipsis',
      ...props.style,
    }}
  >
    <B.Html as="span" html={title} variant="toc" />
  </TocEntryText>
)

const TocEntryPad = props => (
  <B.Box flex="1 0 1em" borderBottom="dotted" mx="xs" {...props} />
)

const TocEntryPageNum = props => <TocEntryText flex="0 0 auto" {...props} />

const LinedTocEntry = ({index, showPageNums, ...props}) => {
  const {entry} = props
  return (
    <TocEntryRow borderTop="std" py="1rem" {...props}>
      <TocEntryTitle level={entry.level} title={entry.title} />
      {showPageNums && (
        <TocEntryPageNum pl="1em">{entry.logicalPage}</TocEntryPageNum>
      )}
    </TocEntryRow>
  )
}

const PlainTocEntry = ({index, showPageNums, ...props}) => {
  const {entry} = props
  return (
    <TocEntryRow py=".25rem" {...props}>
      <TocEntryTitle
        level={entry.level}
        title={entry.title}
        flex={showPageNums ? '0 1 auto' : '0 1 100%'}
        style={{
          textOverflow: showPageNums ? 'clip' : 'ellipsis',
        }}
      />
      {showPageNums && (
        <>
          <TocEntryPad />
          <TocEntryPageNum>{entry.logicalPage}</TocEntryPageNum>
        </>
      )}
    </TocEntryRow>
  )
}

const BarsTocEntry = ({index, showPageNums, ...props}) => {
  const {entry} = props
  return (
    <TocEntryRow
      py=".75rem"
      backgroundColor={index % 2 === 0 && 'backgroundColorLight'}
      {...props}
    >
      <TocEntryTitle level={entry.level} title={entry.title} />
      {showPageNums && <TocEntryPageNum>{entry.logicalPage}</TocEntryPageNum>}
    </TocEntryRow>
  )
}

const tocEntryComponents = {
  lined: LinedTocEntry,
  plain: PlainTocEntry,
  bars: BarsTocEntry,
}

export const TocBlock = B.blockWithInfo(
  {
    name: 'content-toc',
    displayName: 'TocBlock',
    title: 'Table of Contents',
    Admin: TocAdmin,
    defaultConfig,
  },
  ({config: {heading, showPageNums, style}}) => {
    const context = B.useBlockContext()
    const metaObj = B.useMetaObj(context)
    const rawEntries = B.useApi.toc({tizraId: context.tizraId}).data
    const TocEntry = tocEntryComponents[style] || tocEntryComponents.plain

    // Attach parent for the sake of meta.href linking to url-id.
    // https://github.com/Tizra/frontend/issues/303
    const entries = useMemo(
      () =>
        rawEntries
          ?.filter(entry => entry.isDisplayed)
          .map(entry => ({...entry, parent: metaObj})),
      [rawEntries, metaObj],
    )

    // The ToC needs more than one bookmark or it's bogus. This can happen
    // especially for publications that end up with a single bookmark representing
    // the entire document, for example:
    //
    //   [{title: "foo.pdf", level: 0, pageNumber: 1}]
    //
    // https://github.com/Tizra/quickstart-v3/issues/245
    const content =
      !entries || entries.length < 2
        ? null
        : entries.map((entry, index) => (
            <TocEntry
              key={entry.id}
              index={index}
              entry={entry}
              showPageNums={showPageNums}
            />
          ))

    return (
      <B.Scene>
        {content && <B.Section heading={heading}>{content}</B.Section>}
      </B.Scene>
    )
  },
)
